import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { theme, Typo, Icon } from '../../../ui';
import { toRgba } from '../../../ui/theme/colors';

const MenuContainer = styled.div`
  position: fixed;
  top: ${({ $position }) => $position.y}px;
  left: ${({ $position }) => $position.x}px;
  background: ${theme.colors.NEU_0};
  border: 1px solid ${theme.colors.NEU_600};
  border-radius: 8px;
  box-shadow: 0 4px 12px ${toRgba(theme.colors.NEU_999, 0.15)};
  min-width: 220px;
  z-index: 10000;
  padding: 4px 0;
`;

const MenuItem = styled.div`
  padding: 10px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: background-color 0.15s;

  &:hover {
    background-color: ${theme.colors.NEU_100};
  }

  ${({ $disabled }) =>
    $disabled &&
    `
    opacity: 0.5;
    cursor: not-allowed;
    &:hover {
      background-color: transparent;
    }
  `}

  ${({ $danger }) =>
    $danger &&
    `
    color: ${theme.colors.ACTION};
    &:hover {
      background-color: ${theme.colors.ACTION_LIGHT};
    }
  `}
`;

const MenuDivider = styled.div`
  height: 1px;
  background-color: ${theme.colors.NEU_600};
  margin: 4px 0;
`;

const ShortcutLabel = styled.span`
  margin-left: auto;
  font-size: 12px;
  color: ${theme.colors.NEU_600};
  font-family: monospace;
`;

const IconWrapper = styled.div`
  width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ContextMenu = ({ position, items, onClose }) => {
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Adjust position if menu would go off-screen
  useEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let adjustedX = position.x;
      let adjustedY = position.y;

      if (rect.right > viewportWidth) {
        adjustedX = viewportWidth - rect.width - 10;
      }

      if (rect.bottom > viewportHeight) {
        adjustedY = viewportHeight - rect.height - 10;
      }

      if (adjustedX !== position.x || adjustedY !== position.y) {
        menuRef.current.style.left = `${adjustedX}px`;
        menuRef.current.style.top = `${adjustedY}px`;
      }
    }
  }, [position]);

  const handleItemClick = (item) => {
    if (!item.disabled && item.onClick) {
      item.onClick();
      onClose();
    }
  };

  const renderIcon = (iconName) => {
    const IconComponent = Icon[iconName];
    return IconComponent ? <IconComponent size='x-small' /> : null;
  };

  return (
    <MenuContainer ref={menuRef} $position={position}>
      {items.map((item, index) => {
        if (item.type === 'divider') {
          return <MenuDivider key={`divider-${index}`} />;
        }

        return (
          <MenuItem key={item.label} onClick={() => handleItemClick(item)} $disabled={item.disabled} $danger={item.danger}>
            <IconWrapper>{item.icon && renderIcon(item.icon)}</IconWrapper>
            <Typo.SmallText>{item.label}</Typo.SmallText>
            {item.shortcut && <ShortcutLabel>{item.shortcut}</ShortcutLabel>}
          </MenuItem>
        );
      })}
    </MenuContainer>
  );
};

export default ContextMenu;
